/**
 * Onboarding Modal Component
 * Feature 024: Care Seeker Dashboard - First-time onboarding guidance
 *
 * Displays helpful guidance for new care seekers on how to get started
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
}

export function OnboardingModal({ open, onClose }: OnboardingModalProps) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-gray-900">
            How to get started
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <p className="text-gray-700 text-base">
            Choose the path that feels right for you today. You can change course at any time.
          </p>

          {/* Smart Search Option */}
          <div className="border border-gray-200 rounded-lg p-6 hover:border-[#026857] transition-colors">
            <div className="mb-3">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                💬 Start with Smart Search (Guided Support)
              </h3>
              <ul className="space-y-2 text-gray-700 text-base">
                <li>• Have a simple conversation with our AI-guided Care Coordinator</li>
                <li>• Share what you're navigating—no prep or "right" answers needed</li>
                <li>• Receive personalized recommendations and clear next steps tailored to your situation</li>
              </ul>
            </div>
          </div>

          {/* Pro Directory Option */}
          <div className="border border-gray-200 rounded-lg p-6 hover:border-[#026857] transition-colors">
            <div className="mb-3">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                🔍 Explore the Pro Directory (Self-Directed)
              </h3>
              <ul className="space-y-2 text-gray-700 text-base">
                <li>• Search our vetted network of professional service providers</li>
                <li>• Filter by location, specialty, and type of support</li>
                <li>• Connect directly with experts when you're ready</li>
              </ul>
            </div>
          </div>

          {/* Resource Hub Option */}
          <div className="border border-gray-200 rounded-lg p-6 hover:border-[#026857] transition-colors">
            <div className="mb-3">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                📚 Browse the Resource Hub (Learn & Understand)
              </h3>
              <ul className="space-y-2 text-gray-700 text-base">
                <li>• Access a curated digital library of articles, books, videos, and podcasts</li>
                <li>• Content is thoughtfully selected and created by our professional community</li>
                <li>• Learn at your own pace and return anytime</li>
              </ul>
            </div>
          </div>

          {/* Closing Message */}
          <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
            <p className="text-gray-700 text-base mb-3">
              You can move between these options whenever you need. There's no single "right" way to use bQuest—just the way that works best for you, right now.
            </p>
            <p className="text-gray-700 text-base">
              And if you ever feel off track or unsure about what to do next, our Care Concierge is always here to help at{' '}
              <a
                href="mailto:careconcierge@thebquest.com"
                className="text-[#026857] hover:underline font-medium"
              >
                careconcierge@thebquest.com
              </a>
            </p>
          </div>

          {/* Close Button */}
          <div className="flex justify-end pt-2">
            <Button
              onClick={onClose}
              className="bg-[#026857] hover:bg-[#025247] text-white"
            >
              Got it, let's get started
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
